import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import api, { eur, apiErr } from "@/lib/api";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { toast } from "sonner";

const inp = "w-full bg-[#0A0A0B] border border-zinc-700 rounded px-3 py-2.5 text-sm outline-none focus:border-[#FF2E4C]";

export function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const load = () => api.get("/categories").then(({ data }) => setCats(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => { if (!name.trim()) return; await api.post("/admin/categories", { name, greek_name: name, order: cats.length + 1 }); setName(""); load(); toast.success("Κατηγορία προστέθηκε"); };
  const del = async (id) => { await api.delete(`/admin/categories/${id}`); load(); };
  return (
    <div className="p-6 lg:p-10">
      <AdminHeader title="Categories" subtitle="ΚΑΤΗΓΟΡΙΕΣ" />
      <div className="flex gap-2 mb-6 max-w-md"><input value={name} onChange={(e) => setName(e.target.value)} placeholder="Νέα κατηγορία" className={inp} data-testid="cat-input" /><button onClick={add} className="btn-red px-5 rounded text-xs font-bold uppercase flex items-center gap-1" data-testid="cat-add"><Plus className="w-4 h-4" /></button></div>
      <div className="card-surface rounded-lg divide-y divide-zinc-800 max-w-md">
        {cats.map((c) => <div key={c.id} className="flex items-center justify-between p-4"><span>{c.name}</span><button onClick={() => del(c.id)} className="text-zinc-600 hover:text-red-500"><Trash2 className="w-4 h-4" /></button></div>)}
      </div>
    </div>
  );
}

export function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [f, setF] = useState({ code: "", type: "percentage", value: 10, min_order: 0, usage_limit: "", expires_at: "", active: true });
  const load = () => api.get("/admin/coupons").then(({ data }) => setCoupons(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.code.trim()) { toast.error("Δώσε κωδικό"); return; }
    try { await api.post("/admin/coupons", { ...f, value: parseFloat(f.value) || 0, min_order: parseFloat(f.min_order) || 0, usage_limit: f.usage_limit ? parseInt(f.usage_limit) : null, expires_at: f.expires_at || null }); setF({ code: "", type: "percentage", value: 10, min_order: 0, usage_limit: "", expires_at: "", active: true }); load(); toast.success("Κουπόνι δημιουργήθηκε"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { await api.delete(`/admin/coupons/${id}`); load(); };
  const label = (c) => c.type === "percentage" ? `${c.value}%` : c.type === "fixed" ? eur(c.value) : "Δωρεάν Μεταφορικά";
  return (
    <div className="p-6 lg:p-10">
      <AdminHeader title="Coupons" subtitle="ΚΟΥΠΟΝΙΑ" />
      <div className="card-surface rounded-lg p-5 mb-6">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="ΚΩΔΙΚΟΣ" className={inp} data-testid="coupon-code" />
          <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className={inp}><option value="percentage">Ποσοστό %</option><option value="fixed">Σταθερό €</option><option value="free_shipping">Δωρεάν Μεταφορικά</option></select>
          <input type="number" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} placeholder="Τιμή" className={inp} disabled={f.type === "free_shipping"} data-testid="coupon-value" />
          <input type="number" value={f.min_order} onChange={(e) => setF({ ...f, min_order: e.target.value })} placeholder="Ελάχ. παραγγελία €" className={inp} />
          <input type="number" value={f.usage_limit} onChange={(e) => setF({ ...f, usage_limit: e.target.value })} placeholder="Όριο χρήσης (κενό=∞)" className={inp} />
          <input type="date" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })} className={inp} />
        </div>
        <button onClick={add} className="btn-red mt-4 px-6 py-2.5 text-xs font-bold uppercase rounded flex items-center gap-2" data-testid="coupon-add"><Plus className="w-4 h-4" /> ΔΗΜΙΟΥΡΓΙΑ</button>
      </div>
      <div className="card-surface rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead><tr className="text-left text-zinc-500 font-mono text-xs uppercase border-b border-zinc-800"><th className="p-4">Κωδικός</th><th className="p-4">Έκπτωση</th><th className="p-4">Ελάχιστο</th><th className="p-4">Χρήσεις</th><th className="p-4">Λήξη</th><th className="p-4"></th></tr></thead>
          <tbody className="divide-y divide-zinc-800">
            {coupons.map((c) => <tr key={c.id}><td className="p-4 font-mono font-bold text-[#FF2E4C]">{c.code}</td><td className="p-4">{label(c)}</td><td className="p-4">{c.min_order ? eur(c.min_order) : "-"}</td><td className="p-4">{c.used_count}/{c.usage_limit ?? "∞"}</td><td className="p-4 text-xs text-zinc-400">{c.expires_at ? new Date(c.expires_at).toLocaleDateString("el-GR") : "-"}</td><td className="p-4"><button onClick={() => del(c.id)} className="text-zinc-600 hover:text-red-500"><Trash2 className="w-4 h-4" /></button></td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminShipping() {
  const [methods, setMethods] = useState([]);
  const load = () => api.get("/admin/shipping-methods").then(({ data }) => setMethods(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async (m) => { await api.put(`/admin/shipping-methods/${m.id}`, m); toast.success("Ενημερώθηκε"); load(); };
  const upd = (id, k, v) => setMethods((ms) => ms.map((m) => m.id === id ? { ...m, [k]: k === "price" || k === "cod_fee" ? parseFloat(v) || 0 : v } : m));
  return (
    <div className="p-6 lg:p-10">
      <AdminHeader title="Shipping" subtitle="ΜΕΤΑΦΟΡΙΚΑ" />
      <div className="space-y-3 max-w-2xl">
        {methods.map((m) => (
          <div key={m.id} className="card-surface rounded-lg p-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[160px]"><p className="font-semibold text-sm">{m.name}</p><p className="text-xs text-zinc-500">{m.greek_name}</p></div>
            <div><label className="text-[10px] font-mono uppercase text-zinc-500 block">Τιμή €</label><input type="number" step="0.01" value={m.price} onChange={(e) => upd(m.id, "price", e.target.value)} className="w-20 bg-[#0A0A0B] border border-zinc-700 rounded px-2 py-1.5 text-sm" data-testid={`ship-price-${m.type}`} /></div>
            <div><label className="text-[10px] font-mono uppercase text-zinc-500 block">COD €</label><input type="number" step="0.01" value={m.cod_fee} onChange={(e) => upd(m.id, "cod_fee", e.target.value)} className="w-20 bg-[#0A0A0B] border border-zinc-700 rounded px-2 py-1.5 text-sm" /></div>
            <button onClick={() => save(m)} className="btn-red px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-1" data-testid={`ship-save-${m.type}`}><Save className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminInventory() {
  const [products, setProducts] = useState([]);
  const load = () => api.get("/products").then(({ data }) => setProducts(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const updStock = (pid, color, size, stock) => setProducts((ps) => ps.map((p) => p.id === pid ? { ...p, variants: p.variants.map((v) => v.color === color && v.size === size ? { ...v, stock: parseInt(stock) || 0 } : v) } : p));
  const save = async (p) => { await api.put(`/admin/products/${p.id}`, p); toast.success(`${p.name} ενημερώθηκε`); load(); };
  return (
    <div className="p-6 lg:p-10">
      <AdminHeader title="Inventory" subtitle="ΑΠΟΘΕΜΑ ΑΝΑ ΠΑΡΑΛΛΑΓΗ" />
      <div className="space-y-4">
        {products.map((p) => (
          <div key={p.id} className="card-surface rounded-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3"><img src={p.image_front} alt="" className="w-10 h-12 object-cover rounded" /><span className="font-display font-bold uppercase text-sm">{p.name}</span></div>
              <button onClick={() => save(p)} className="btn-red px-4 py-2 rounded text-xs font-bold uppercase flex items-center gap-1" data-testid={`inv-save-${p.slug}`}><Save className="w-4 h-4" /> ΑΠΟΘΗΚΕΥΣΗ</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
              {p.variants.map((v) => (
                <div key={`${v.color}|${v.size}`} className={`flex items-center gap-2 rounded p-2 ${v.stock === 0 ? "bg-red-500/10" : "bg-[#0A0A0B]"}`}>
                  <span className="text-xs flex-1 truncate">{v.color}/{v.size}</span>
                  <input type="number" value={v.stock} onChange={(e) => updStock(p.id, v.color, v.size, e.target.value)} className="w-12 bg-transparent border border-zinc-700 rounded px-1 py-1 text-xs text-center" data-testid={`inv-stock-${p.slug}-${v.color}-${v.size}`} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
